import React, { useState, useRef, useEffect } from "react";
import {
  Upload,
  message,
  Button,
  Modal,
  Select,
  Form,
  Input,
} from "antd";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import { getUploadUrlApi } from "../../../../services/common.services";
import { convertToFormData } from "../../../../helpers/utility";
import useQuery from "../../../../hooks/useQuery";
import { CameraOutlined } from "@ant-design/icons";
import axios from "axios";
import Compressor from "compressorjs";
import { folderUploadApi } from "../../../../services/op.service";
import modalStyles from "../../../PatientsIP/PatientGallery/galleryCompactModal.module.css";
import { getDefaultUploadFileName } from "../../../../helpers/galleryUtils";
import UploadFilePreviewList from "../../../PatientsIP/PatientGallery/UploadFilePreviewList";

const { Dragger } = Upload;

const PatientGalleryUpload = ({
  refresh,
  isModalVisible,
  setIsModalVisible,
  folder,
  patientData,
  labels,
}) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [uploadFileLoading, setUploadFileLoading] = useState(false);
  const [uploadFile, setUploadFile] = useState([]);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia("(max-width: 768px)").matches
      : false
  );

  const params = useQuery();

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const handleRemoveFile = (fileToRemove) => {
    if (fileToRemove?.preview_url?.startsWith("blob:")) {
      URL.revokeObjectURL(fileToRemove.preview_url);
    }

    const filteredFiles = uploadFile.filter(
      (file) => file.path !== fileToRemove.path
    );

    setUploadFile(filteredFiles);

    if (filteredFiles.length === 0) {
      setUploadFileLoading(false);
    }
    message.success("Removed file successfully");
  };

  const handleFileNameChange = (path, value) => {
    setUploadFile((prev) =>
      prev.map((file) =>
        file.path === path ? { ...file, file_name: value } : file
      )
    );
  };

  const uploadProps = {
    beforeUpload: (file) => {
      return new Promise((resolve, reject) => {
        let file_ext = file.name.split(".").slice(-1)[0];
        const validImageTypes = ["png", "jpg", "jpeg", "svg"];
        const validVideoTypes = ["mp4", "mov"];
        const validPdfTypes = ["pdf"];

        const fileType =
          validImageTypes.includes(file_ext) ||
          validVideoTypes.includes(file_ext) ||
          validPdfTypes.includes(file_ext);

        if (!fileType) {
          message.error(`${file.name} is not a valid file type`);
          return reject(Upload.LIST_IGNORE);
        }

        if (validImageTypes.includes(file_ext)) {
          new Compressor(file, {
            convertSize: 1000000,
            quality: 0.8,
            success(compressedFile) {
              resolve(compressedFile);
            },
            error(err) {
              console.error("Compression failed:", err.message);
              reject(err);
            },
          });
        } else {
          resolve(file);
        }
      });
    },
    async onChange(info) {
      if (info.file.status === "done") {
        setUploadFileLoading(true);
        try {
          const res = await getUploadUrlApi(
            `path_key=${info.file.name}&admission_type=OP&admission_id=${patientData?.op_id}`
          );
          const formData = convertToFormData(res, info);
          await axios({
            method: "POST",
            url: res?.data?.url,
            data: formData,
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          setUploadFile((prevState) => [
            ...prevState,
            {
              path: res?.data?.fields?.key,
              type: info.file.type,
              file_name: getDefaultUploadFileName(info.file),
              preview_url: URL.createObjectURL(
                info.file.originFileObj || info.file
              ),
            },
          ]);
        } catch (err) {
          message.error(err?.response?.data?.message);
        } finally {
          setUploadFileLoading(false);
        }
      }
    },
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const onFinish = async (val) => {
    const finalFolder = folder || val?.selected_folder || null;
    const payload = {
      folder: finalFolder,
      paths: uploadFile.map((file) => ({
        path: file.path,
        type: file.type,
        file_name: file.file_name?.trim() || file.path.split("/").pop(),
      })),
      admission: params?.admission,
    };

    if (val?.sub_folder) {
      payload.sub_folder = val?.sub_folder;
    }

    folderUploadApi(payload)
      .then((res) => {
        // message.success(res?.data?.message);
        console.log(res);
        refresh();
        setIsModalVisible(false);
      })
      .catch((err) => {
        console.log(err);
        message.error(err?.response?.data?.message);
      });

    // uploadPatientImagesApi(formData)
    //   .then((response) => {
    //     message.success(response.data.message);
    //     refresh();
    //     setIsModalVisible(false);
    //   })
    //   .catch((error) => {
    //     message.error(error.response.data.message);
    //   });
  };

  // const handleLabelSelect = (value, option) => {
  //   setSelectedFolder(option.value);
  // };

  // const onLabelAdd = (values) => {
  //   const payload = {
  //     name: newLabel,
  //   };

  //   addCategoryApi(payload)
  //     ?.then((res) => {
  //       message.success(res?.data?.message);
  //       refreshLabelsPage();
  //       setNewLabel(null);
  //     })
  //     .catch((err) => message.error(err?.response?.data?.message));
  // };
  return (
    <>
      <Modal
        width={isMobile ? "min(450px, calc(100vw - 24px))" : 450}
        {...(isMobile ? { centered: false } : {})}
        wrapClassName={
          isMobile
            ? `${modalStyles.compactModalWrap} ${modalStyles.galleryUploadModalWrap}`
            : undefined
        }
        styles={{
          content: isMobile
            ? {
                display: "flex",
                flexDirection: "column",
                maxHeight: "calc(100dvh - 32px)",
                overflow: "hidden",
              }
            : undefined,
          body: isMobile
            ? {
                flex: "1 1 auto",
                minHeight: 0,
                maxWidth: "100%",
                overflowX: "hidden",
                overflowY: "auto",
                boxSizing: "border-box",
              }
            : undefined,
          footer: isMobile
            ? {
                flexShrink: 0,
                marginTop: 0,
                background: "#fff",
                borderTop: "1px solid #f0f0f0",
              }
            : undefined,
        }}
        title={`Upload ${folder ? "to " + folder : ""}`}
        onCancel={() => setIsModalVisible(false)}
        open={isModalVisible}
        footer={
          <div
            className="flex-end"
            style={{
              gap: isMobile ? 12 : 24,
              marginTop: isMobile ? 0 : 20,
            }}
          >
            <Button
              type="primary"
              ghost
              style={{ fontWeight: "500" }}
              className={btnStyles.mdBtn}
              onClick={() => setIsModalVisible(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={uploadFileLoading}
              type="primary"
              style={{ fontWeight: "500" }}
              className={btnStyles.mdBtn}
              onClick={() => {
                if (formRef.current) {
                  formRef.current.submit();
                }
              }}
            >
              Save
            </Button>
          </div>
        }
      >
        <Form
          layout="vertical"
          form={form}
          ref={formRef}
          onFinish={onFinish}
          className={isMobile ? modalStyles.compactForm : undefined}
        >
          {!folder && (
            <Form.Item
              label="Select Label"
              name="selected_folder"
              rules={[{ required: true, message: "Please select a label" }]}
            >
              <Select
                placeholder="Select label"
                style={isMobile ? { width: "100%" } : undefined}
              >
                {labels?.map((label) => (
                  <Select.Option key={label} value={label}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}

          <Form.Item label="Sub Folder" name="sub_folder">
            <Input />
            {/* <Select
              showSearch
              optionFilterProp="children"
              placeholder="Search Labels..."
              onSelect={handleLabelSelect}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: "8px 0" }} />
                  <Space style={{ padding: "0 8px 4px" }}>
                    <Input
                      placeholder="Add new label"
                      ref={inputRef}
                      value={newLabel}
                      onChange={(e) => setNewLabel(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={onLabelAdd}
                    >
                      Add
                    </Button>
                  </Space>
                </>
              )}
            >
              {labels?.map((label) => (
                <Option key={label?.id} value={label?.name}>
                  {label?.name}
                </Option>
              ))}
            </Select> */}
          </Form.Item>
          <Form.Item
            label="Upload Gallery"
            name="upload"
            rules={[
              {
                required: uploadFile?.length === 0 ? true : false,
                message: "Please upload files",
              },
            ]}
          >
            <Dragger
              className={isMobile ? modalStyles.compactDragger : undefined}
              style={
                isMobile
                  ? {
                      width: "100%",
                      maxWidth: "100%",
                      boxSizing: "border-box",
                    }
                  : { width: "400px" }
              }
              accept=".png,.jpg,.jpeg,.svg,.mp4,.mov,.avi,.pdf"
              {...uploadProps}
              multiple
              customRequest={dummyRequest}
              showUploadList={false}
            >
              <p className="ant-upload-drag-icon">
                <CameraOutlined />
              </p>
              <p className="ant-upload-text">
                <span style={{ color: "#406AFF" }}>Click to Capture </span> or
                upload
              </p>
              {/* <p className="ant-upload-hint">
                PDF, SVG, JPEG, JPG, PNG, MP4, MOV, AVI
              </p> */}
            </Dragger>
            {isMobile ? (
              <div className={modalStyles.uploadFileListScroll}>
                <UploadFilePreviewList
                  uploadFile={uploadFile}
                  onRemove={handleRemoveFile}
                  onFileNameChange={handleFileNameChange}
                  isMobile={isMobile}
                />
              </div>
            ) : (
              <UploadFilePreviewList
                uploadFile={uploadFile}
                onRemove={handleRemoveFile}
                onFileNameChange={handleFileNameChange}
                isMobile={isMobile}
              />
            )}
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PatientGalleryUpload;
